import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ActivityIndicator, ScrollView, Text } from "react-native";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import PostCard from "@/src/components/posts/post-card";
import { FeedReactionsProvider } from "@/src/hooks/features/posts/use-feed-reactions";
import ScButton from "@/src/components/ui-kit/button";
import ScSearchInput from "@/src/components/ui-kit/search-bar";
import FeedSortControl from "@/src/components/posts/feed-sort-control";
import { useFeedDemand } from "@/src/hooks/common/use-feed-demand";
import type { FeedMode, FeedSort } from "@/src/utils/feed-sort";
import type { Post } from "@/src/models/post.model";
import { useFollowingScope } from "./hooks";
import {
  getControls,
  directCards,
  readProtected,
  rejectedAuthors,
} from "./service";
import { isSafeNongroup } from "./contract";
import { POSTS_QUERY_KEY } from "@/src/query-keys/post.keys";
export default function FollowingFeed({
  onModeChange,
}: {
  /** Switch the top-level feed selector (Hottest | Latest | Following). */
  onModeChange?: (mode: FeedMode) => void;
} = {}) {
  const s = useFollowingScope(),
    { t } = useTranslation();
  // Following keeps its existing default order (newest first).
  const sort: FeedSort = "latest";
  const [search, setSearch] = useState(""),
    [term, setTerm] = useState("");
  const controls = useQuery({
    queryKey: [...s.key, "controls"],
    queryFn: () => getControls(s.viewer, s.org),
    enabled: s.available,
    retry: false,
    refetchInterval: 15000,
    meta: {
      persist: false,
    },
  });
  const revision = (controls.data ?? [])
    .map((c) => c.authorId + ":" + c.revision)
    .sort()
    .join("|");
  const rejected = useQuery({
    queryKey: [...s.key, "rejected", revision],
    queryFn: () => rejectedAuthors(s.viewer, s.org, controls.data ?? []),
    enabled: s.available && controls.isSuccess,
    retry: false,
    refetchInterval: 15000,
    meta: { persist: false },
  });
  const authors = useMemo(
    () =>
      (controls.data ?? [])
        .filter(
          (c) => c.desiredFollowing && !rejected.data?.includes(c.authorId),
        )
        .map((c) => c.authorId),
    [controls.data, rejected.data],
  );
  const base = [
    POSTS_QUERY_KEY,
    ...s.key,
    "cards",
    revision,
    (rejected.data ?? []).slice().sort().join("|"),
    sort,
    term,
  ];
  const direct = useInfiniteQuery({
    queryKey: [...base, "direct"],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => ({
      getposts: {
        items: await directCards(
          s.viewer,
          s.org,
          authors,
          pageParam,
          term,
          sort,
        ),
      },
    }),
    getNextPageParam: (last, pages) =>
      last.getposts.items.length === 5 ? pages.length + 1 : undefined,
    enabled: s.available && controls.isSuccess && authors.length > 0,
    retry: false,
    meta: {
      persist: false,
    },
  });
  const protectedCards = useInfiniteQuery({
    refetchInterval: 30000,
    queryKey: [...base, "protected"],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const result = await readProtected(sort, pageParam, term);
      return {
        getposts: {
          items: result.items,
          nextCursor: result.nextCursor,
        },
      };
    },
    getNextPageParam: (last) => last.getposts.nextCursor ?? undefined,
    enabled: s.available && controls.isSuccess && authors.length > 0,
    retry: false,
    meta: {
      persist: false,
    },
  });
  const [shown, setShown] = useState(1),
    oldScope = useRef("");
  const scope = JSON.stringify(base);
  useEffect(() => {
    if (oldScope.current !== scope) {
      oldScope.current = scope;
      setShown(1);
    }
  }, [scope]);
  const prefetch = useCallback(() => {
    if (
      direct.hasNextPage &&
      !direct.isFetchingNextPage &&
      direct.data &&
      direct.data.pages.length <= shown
    )
      void direct.fetchNextPage();
    if (
      protectedCards.hasNextPage &&
      !protectedCards.isFetchingNextPage &&
      protectedCards.data &&
      protectedCards.data.pages.length <= shown
    )
      void protectedCards.fetchNextPage();
  }, [direct, protectedCards, shown]);
  const load = useCallback(() => {
    setShown((n) => n + 1);
    prefetch();
  }, [prefetch]);
  useEffect(() => {
    if (direct.data?.pages.length === 1) prefetch();
  }, [direct.data?.pages.length, prefetch]);
  const demand = useFeedDemand({
    scope,
    busy: direct.isFetchingNextPage || protectedCards.isFetchingNextPage,
    hasMore:
      !!direct.hasNextPage ||
      !!protectedCards.hasNextPage ||
      (direct.data?.pages.length ?? 0) > shown ||
      (protectedCards.data?.pages.length ?? 0) > shown,
    loadMore: load,
    prefetchMore: prefetch,
  });
  // Keep original direct card state when enrichment arrives; appended protected cards never reorder visible rows.
  const visible: Post[] = [];
  const ids = new Set<string>();
  for (const post of direct.data?.pages
    .slice(0, shown)
    .flatMap((p) => p.getposts.items) ?? []) {
    if (
      authors.includes(post.CreatedBy) &&
      isSafeNongroup(post, s.viewer) &&
      !ids.has(post.ItemId)
    ) {
      ids.add(post.ItemId);
      visible.push(post);
    }
  }
  for (const post of (!protectedCards.isError && !controls.isError
    ? protectedCards.data
    : undefined
  )?.pages
    .slice(0, shown)
    .flatMap((p) => p.getposts.items) ?? []) {
    if (authors.includes(post.CreatedBy) && !ids.has(post.ItemId)) {
      ids.add(post.ItemId);
      visible.push(post);
    }
  }
  if (!s.available)
    return <Text className="text-slate">{t("FOLLOW_UNAVAILABLE")}</Text>;
  return (
    <ScrollView
      onScroll={demand.onScroll}
      onLayout={demand.onLayout}
      onContentSizeChange={demand.onContentSizeChange}
      scrollEventThrottle={16}
      contentContainerClassName="gap-4 pb-6"
    >
      <ScSearchInput
        value={search}
        onChangeText={setSearch}
        handleSearch={() => setTerm(search.trim())}
        placeholderKey="FOLLOW_SEARCH"
      />
      <FeedSortControl
        sort="following"
        showFollowing
        onSortChange={(mode) => mode !== "following" && onModeChange?.(mode)}
      />
      {controls.isPending && <ActivityIndicator />}
      {controls.isError && (
        <ScButton
          text="BTN_RETRY"
          onPress={() => {
            void controls.refetch();
          }}
        />
      )}
      {controls.isSuccess && !authors.length && (
        <Text className="text-slate p-4">{t("FOLLOW_NONE")}</Text>
      )}
      {direct.isPending && authors.length > 0 && <ActivityIndicator />}
      {!visible.length && authors.length > 0 && !direct.isPending && (
        <Text className="text-slate p-4">{t("FOLLOW_NO_POSTS")}</Text>
      )}
      {direct.isError && (
        <ScButton
          text="BTN_RETRY"
          onPress={() => {
            void direct.refetch();
          }}
        />
      )}
      <FeedReactionsProvider postIds={visible.map((p) => p.ItemId)}>
        {visible.map((post) => (
          <PostCard key={post.ItemId} post={post} />
        ))}
      </FeedReactionsProvider>
      {protectedCards.isError && authors.length > 0 && (
        <ScButton
          mode="text"
          text="FOLLOW_RETRY_ENRICHMENT"
          onPress={() => {
            void protectedCards.refetch();
          }}
        />
      )}
      {(direct.isFetchingNextPage || protectedCards.isFetchingNextPage) && (
        <ActivityIndicator />
      )}
      {demand &&
        (direct.hasNextPage ||
          protectedCards.hasNextPage ||
          (direct.data?.pages.length ?? 0) > shown ||
          (protectedCards.data?.pages.length ?? 0) > shown) && (
          <ScButton
            text="BTN_LOAD_MORE"
            style={{
              minHeight: 44,
            }}
            onPress={demand.manualLoadMore}
          />
        )}
    </ScrollView>
  );
}
